import { laWhen, money as formatMoneyFallback } from './format.js';
/*
 * Модель отображения экрана «Кампании» v2 (макет Main): вкладки, день,
 * дни паузы, цена покупки против цели, причина паузы. Всё чистое — без React —
 * чтобы экран, сортировка детей и тесты читали один источник правил.
 */
/** Цель по цене покупки: $25 (решение владельца; в центах). */
export const TARGET_CPP_MINOR = 2500;
/** Порог окупаемости «Вернётся на 1 $» — пунктир полосы (как у «Окупаемости» по умолчанию). */
export const PAYBACK_THRESHOLD = 1.08;
export const PAYBACK_SCALE_MAX = 2.5;
/** Объект в паузе меньше стольких дней нельзя запускать; с 4-го — можно. */
export const PAUSE_NO_LAUNCH_MAX_DAYS = 3;
export const PAUSE_CAN_LAUNCH_MAX_DAYS = 5;
/** Статусы старше стольких минут показываются жёлтым («устарели»). */
export const STATUSES_STALE_MIN = 30;
// ── вкладки ──────────────────────────────────────────────────────────────
export const CAMPAIGN_TABS = ['live', 'all', 'running', 'paused', 'archived', 'attention'];
export const DEFAULT_TAB = 'live';
export const TAB_LABEL = {
    all: 'Все',
    live: 'Работают и на паузе',
    running: 'Работают',
    paused: 'На паузе',
    archived: 'Архив',
    attention: 'Требуют внимания',
};
/**
 * Вкладка → параметры списка auto-ads (A3): `state` = running|active|paused|all|attention и
 * `archived` — только архивные; флаг `archived=true` добавляет архив к «Все».
 */
export function tabQuery(tab) {
    switch (tab) {
        case 'all':
            return { state: 'all', archived: true };
        case 'live':
            return { state: 'all' };
        case 'running':
            return { state: 'running' };
        case 'paused':
            return { state: 'paused' };
        case 'archived':
            return { state: 'archived' };
        case 'attention':
            return { state: 'attention' };
    }
}
/** Ключи `counts` ответа под вкладки. */
export const TAB_COUNT_KEY = {
    all: 'all',
    live: 'runningAndPaused',
    running: 'running',
    paused: 'paused',
    archived: 'archived',
    attention: 'attention',
};
/** Сортировка, показанная на шапке, когда в адресе её нет: так сервер сортирует «по умолчанию» (макет: «Статус ↓»). */
export const DEFAULT_SORT_SHOWN = { key: 'status', dir: 'desc' };
/** Колонки, которые при первом клике идут от меньшего к большему (имя, цена, дни паузы). */
export const ASC_FIRST = new Set([
    'name',
    'pauseDays',
    'cppD1',
    'cppD7',
    'cppD30',
    'cppD90',
    'cppToday',
]);
// ── день ─────────────────────────────────────────────────────────────────
/** Сегодня по Лос-Анджелесу (`YYYY-MM-DD`): день Meta и «Окупаемости». */
export function laToday(now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
export const isDay = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
export function shiftDay(day, delta) {
    const [y, m, d] = day.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + delta));
    return t.toISOString().slice(0, 10);
}
/** «03.11» из `2026-11-03`. */
export const dayShort = (day) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
/** «Сегодня, 03.11» / «Вчера, 02.11» / «пн, 26.10». */
export function dayLabel(day, today) {
    if (day === today)
        return `Сегодня, ${dayShort(day)}`;
    if (day === shiftDay(today, -1))
        return `Вчера, ${dayShort(day)}`;
    const [y, m, d] = day.split('-').map(Number);
    const wd = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
    return `${wd}, ${dayShort(day)}`;
}
/** Минут от ISO-времени до `now`; не время — `null`. */
export function minutesAgo(iso, now = new Date()) {
    if (!iso)
        return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : Math.max(0, Math.round((now.getTime() - t) / 60_000));
}
/** «4 мин назад», «2 ч назад». */
export function agoText(min) {
    if (min < 1)
        return 'только что';
    if (min < 60)
        return `${min} мин назад`;
    const h = Math.floor(min / 60);
    return h < 48 ? `${h} ч назад` : `${Math.floor(h / 24)} дн назад`;
}
export function pauseDaysView(days) {
    if (days === null || days === undefined || !Number.isFinite(days))
        return null;
    if (days <= PAUSE_NO_LAUNCH_MAX_DAYS)
        return {
            tone: 'no',
            text: `✕ ${days} · нельзя`,
            hint: `На паузе ${days} дн — запускать рано (нужно от 4)`,
        };
    if (days <= PAUSE_CAN_LAUNCH_MAX_DAYS)
        return {
            tone: 'yes',
            text: `✓ ${days} · можно`,
            hint: `На паузе ${days} дн — можно запускать`,
        };
    return { tone: 'plain', text: `${days} дн`, hint: `На паузе ${days} дн` };
}
export function cppTone(minor, target = TARGET_CPP_MINOR) {
    if (minor <= target)
        return 'good';
    return minor <= target * 1.15 ? 'warn' : 'bad';
}
export const CPP_TONE_WORD = {
    good: 'не выше цели',
    warn: 'немного выше цели',
    bad: 'выше цели',
};
/** Длина полоски 0…100: вся шкала — две цели. */
export const cppBarPct = (minor, target = TARGET_CPP_MINOR) => Math.round(Math.min(100, Math.max(4, (minor / (target * 2)) * 100)));
// ── причина паузы ────────────────────────────────────────────────────────
const REASON_WORD = {
    rule: 'правило',
    manual: 'вручную',
    'meta-error': 'ошибка Meta',
    'budget-limit': 'лимит бюджета',
    launch: 'запуск «Запуски FB»',
};
/** Вторая строка статуса паузы: слово по виду причины; английский текст auto-ads наружу не идёт, имя правила — да. */
export function pausedReasonLine(r) {
    if (!r)
        return null;
    const word = REASON_WORD[r.kind];
    if (r.kind === 'rule' && r.text && /[А-Яа-яЁё]/.test(r.text))
        return `«${r.text}»`;
    if (r.kind === 'manual' && r.by)
        return `${word} · ${r.by.split('@')[0]}`;
    return word;
}
export const pausedAtText = (r) => r?.at ? (laWhen(r.at)?.slice(6) ?? null) : null;
// ── объект: слова ────────────────────────────────────────────────────────
export const KIND_ICON_TITLE = {
    campaign: 'Кампания',
    adset: 'Группа',
    ad: 'Объявление',
};
// ── деньги ───────────────────────────────────────────────────────────────
const NBSP = ' ';
const groupThousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
/**
 * Деньги по канону макетов: «$24,03» (запятая, неразрывный пробел в тысячах);
 * `whole` — целые («$3 412», «$400»). Не доллары — как в остальной админке.
 */
export function fmtMoney(minor, currency, mode = 'cents') {
    if (currency.toUpperCase() !== 'USD')
        return formatMoneyFallback(minor, currency);
    const neg = minor < 0;
    // дробные центы (расход ÷ покупки) округляем ОДИН раз: 2199,6 → 2200, а не «$21,100»
    const abs = Math.round(Math.abs(minor));
    if (mode === 'whole')
        return `${neg ? '−' : ''}$${groupThousands(Math.round(abs / 100))}`;
    const whole = Math.floor(abs / 100);
    const cents = String(abs % 100).padStart(2, '0');
    return `${neg ? '−' : ''}$${groupThousands(whole)},${cents}`;
}
/** Курс «на 1 $»: «1,41 $». */
export const perDollarText = (v) => `${v.toFixed(2).replace('.', ',')}${NBSP}$`;
//# sourceMappingURL=view.js.map